<script setup>
import { computed } from 'vue';
import CarRentalSection from './CarRentalSection.vue';
import FlightSection from './FlightSection.vue';
import HotelSection from './HotelSection.vue';
import PackageSection from './PackageSection.vue';
import GenericSection from './GenericSection.vue';

const props = defineProps({
  bookingType: { type: String, required: true },
  details: { type: Object, default: () => ({}) },
});

const isObject = value => Boolean(value) && typeof value === 'object';

// A typed section only renders when the stored JSON has that type's shape;
// anything else (old flat manual rows, `other`) falls back to the generic list.
// The markers are the nested/typed keys only: a legacy flat row like
// `{ airline, from }` shares its scalar keys with the typed shape, so gating on
// those would render the typed section and silently drop the extra fields.
const HAS_SHAPE = {
  car_rental: details =>
    details.vehicle || details.pickup || details.dropoff || details.driver,
  flight: details =>
    isObject(details.origin) ||
    isObject(details.destination) ||
    Boolean(details.departureAt),
  hotel: details =>
    Boolean(details.checkIn) ||
    Boolean(details.checkOut) ||
    isObject(details.guests),
  package: details => (details.components || []).length > 0,
};

const SECTIONS = {
  car_rental: CarRentalSection,
  flight: FlightSection,
  hotel: HotelSection,
  package: PackageSection,
};

const section = computed(() => {
  const hasShape = HAS_SHAPE[props.bookingType];
  return hasShape && hasShape(props.details)
    ? SECTIONS[props.bookingType]
    : GenericSection;
});
</script>

<template>
  <component :is="section" :details="details" />
</template>
